<template>
  <div class="hotelux-travelers" v-click-outside="closePanel">
    <div class="travelers-trigger" @click="togglePanel">
      <slot name="icon" />
      <UIText class="trigger-text" :type="isSticky ? '' : 'bold'">
        {{ triggerText }}
      </UIText>
    </div>

    <div v-if="isPanelOpen" class="travelers-panel">
      <div class="guest-row">
        <UIText class="guest-label" type="bold">{{
          t("shared.hotel.search.ROOMS")
        }}</UIText>
        <div class="guest-control">
          <button
            type="button"
            class="step-button"
            :class="{ 'is-disabled': roomReduceDisabled }"
            @click="handleRoomChange(-1)"
          >
            <font-icon
              class="icon-hotelux icon-jia"
              icon="icon-hotelux-remove-min"
            />
          </button>
          <span class="guest-count">{{ rooms }}</span>
          <button
            type="button"
            class="step-button"
            :class="{ 'is-disabled': roomAddDisabled }"
            @click="handleRoomChange(1)"
          >
            <font-icon
              class="icon-hotelux icon-jia"
              icon="icon-hotelux-add-min"
            />
            <!-- <span class="plus-glyph" /> -->
          </button>
        </div>
      </div>

      <div class="panel-divider" />

      <div class="guest-row">
        <UIText class="guest-label" type="bold">
          {{ t("shared.hotel.search.ADULTS") }}
          <UIText class="label-hint">{{ t("shared.common.perRoom") }}</UIText>
        </UIText>
        <div class="guest-control">
          <button
            type="button"
            class="step-button"
            :class="{ 'is-disabled': adultReduceDisabled }"
            @click="handleAdultChange(-1)"
          >
            <font-icon
              class="icon-hotelux icon-jia"
              icon="icon-hotelux-remove-min"
            />
          </button>
          <span class="guest-count">{{ adults }}</span>
          <button
            type="button"
            class="step-button"
            :class="{ 'is-disabled': adultAddDisabled }"
            @click="handleAdultChange(1)"
          >
            <font-icon
              class="icon-hotelux icon-jia"
              icon="icon-hotelux-add-min"
            />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ClickOutside as vClickOutside } from "element-plus";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { useChannelStoreHook } from "@/store/modules/channel";

import UIText from "../UiText/index.vue";

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({})
  },
  // 吸顶态按设计稿回到常规字重
  isSticky: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(["update:modelValue", "change"]);

const { t } = useI18n();
const { channel } = useChannelStoreHook();

const isPanelOpen = ref(false);
const rooms = ref(1);
const adults = ref(1);

const maxRooms = computed(() => channel?.features?.tpHotel?.numberOfRooms);

const roomReduceDisabled = computed(
  () => rooms.value <= 1 || maxRooms.value <= 1
);
const roomAddDisabled = computed(
  () => rooms.value >= maxRooms.value || maxRooms.value <= 1
);
const adultReduceDisabled = computed(() => adults.value <= 1);
const adultAddDisabled = computed(() => adults.value >= 8);

const triggerText = computed(
  () =>
    `${rooms.value} ${t(
      `shared.hotel.search.hotelux.${rooms.value > 1 ? "rooms" : "room"}`
    )}, ${adults.value} ${t(
      `shared.hotel.search.${adults.value > 1 ? "adults" : "adult"}`
    )}`
);

const emitGuest = () => {
  const guest = {
    ...props.modelValue,
    numberOfRooms: rooms.value,
    numberOfAdults: adults.value
  };
  emit("update:modelValue", guest);
  emit("change", guest);
};

const handleRoomChange = step => {
  if (step < 0 && roomReduceDisabled.value) return;
  if (step > 0 && roomAddDisabled.value) return;
  rooms.value += step;
  emitGuest();
};

const handleAdultChange = step => {
  if (step < 0 && adultReduceDisabled.value) return;
  if (step > 0 && adultAddDisabled.value) return;
  adults.value += step;
  emitGuest();
};

const closePanel = () => {
  isPanelOpen.value = false;
};

const togglePanel = () => {
  isPanelOpen.value = !isPanelOpen.value;
};

watch(
  () => props.modelValue,
  value => {
    if (!value) return;
    rooms.value = value.numberOfRooms || 1;
    adults.value = value.numberOfAdults || 1;
  },
  { immediate: true }
);

defineOptions({
  name: "HoteluxTravelers"
});
</script>

<style lang="scss" scoped>
.hotelux-travelers {
  display: flex;

  .travelers-trigger {
    display: flex;
    align-items: center;
    cursor: pointer;
  }

  .trigger-text {
    // font-family: "lato-bold";
    font-size: 16px;
    line-height: 24px;
    color: #212223;
    white-space: nowrap;
  }

  // 弹层挂在搜索栏容器上，与搜索栏右边缘对齐
  .travelers-panel {
    position: absolute;
    // 17px = 16px 间距 + 搜索栏 1px 下描边
    top: calc(100% + 17px);
    right: 0;
    box-sizing: border-box;
    width: 440px;
    padding: 32px 40px;
    background: #fff;
    box-shadow: 0 10px 28px rgb(0 0 0 / 11%);
  }

  .guest-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 42px;
  }

  .guest-label {
    // font-family: "lato-bold";
    font-size: 16px;
    color: #212223;

    .label-hint {
      // font-family: lato;
      font-size: 14px;
      color: #acacac;
    }
  }

  .guest-control {
    display: flex;
    align-items: center;
  }

  // 32 的按钮居中在 42 的格子内，加上中间 48 的计数位共 132 宽
  .step-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    margin: 0 5px;
    padding: 0;
    background: #fff;
    border: 1px solid #d2d3d4;
    cursor: pointer;

    &.is-disabled {
      color: #d2d3d4;
      cursor: default;
    }
  }
  .guest-count {
    width: 48px;
    font-family: "lato-bold";
    font-size: 16px;
    color: #212223;
    text-align: center;
  }

  .panel-divider {
    height: 1px;
    margin: 24px 0;
    background: #f1f2f3;
  }
}
.icon-jia {
  font-size: 10px;
}

// 吸顶态下拉面板按设计稿收窄到 360，内容区随之为 280
.hotelux-search-bar.sticky .travelers-panel {
  width: 360px;
}
</style>
